import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLang, WA_LINK } from "../i18n";
import { Chapter, Reveal } from "./Reveal";

const inputCls =
  "w-full rounded-lg border border-[#EDF1F6]/20 bg-[#07111F]/60 px-4 py-3 text-sm text-[#F5F7FA] placeholder:text-[#EDF1F6]/40 outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]/40 transition-[border-color,box-shadow] duration-300";

export function RequestTransaction() {
  const { t } = useLang();
  const [mode, setMode] = useState("buy");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", mineral: "", qty: "", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const modes = [
    { key: "buy", label: t.request.buy },
    { key: "sell", label: t.request.sell },
    { key: "partnership", label: t.request.partnership },
  ];

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mineral || !form.message.trim()) {
      toast.error(t.request.required);
      return;
    }
    const lines = [
      `MX MINERALS — ${modes.find((m) => m.key === mode).label}`,
      `${t.request.name}: ${form.name}`,
      form.company && `${t.request.company}: ${form.company}`,
      form.email && `${t.request.email}: ${form.email}`,
      form.phone && `${t.request.phone}: ${form.phone}`,
      `${t.request.mineral}: ${form.mineral}`,
      form.qty && `${t.request.qty}: ${form.qty}`,
      `${t.request.message}: ${form.message}`,
    ].filter(Boolean);
    window.open(`${WA_LINK}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
    toast.success(t.request.toast);
  };

  return (
    <section id="request" data-testid="request-section" className="relative py-24 md:py-32 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <img src="/assets/bg-cta.webp" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#07111F]/76" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F] via-transparent to-[#07111F]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(201,162,39,0.10), transparent 70%)" }} />
      </div>
      <div className="relative mx-auto max-w-3xl px-5 md:px-8">
        <Chapter kicker={t.request.kicker} title={t.request.title} subtitle={t.request.subtitle} testId="request" />

        <Reveal>
          <div className="mb-8 flex rounded-full border border-[#EDF1F6]/20 p-1 w-full sm:w-auto" role="group" aria-label="Transaction type">
            {modes.map((m) => (
              <button
                key={m.key}
                data-testid={`request-tab-${m.key}`}
                onClick={() => setMode(m.key)}
                aria-pressed={mode === m.key}
                className={`flex-1 sm:flex-none rounded-full px-6 py-2.5 text-xs font-bold tracking-[0.15em] transition-[background-color,color] duration-300 ${
                  mode === m.key ? "bg-[#C9A227] text-[#07111F]" : "text-[#EDF1F6] hover:text-[#F5F7FA]"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="glass p-7 md:p-10" style={{ borderRadius: "4px 28px 4px 28px" }} data-testid="request-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="request-input-name" required aria-label={t.request.name} placeholder={t.request.name} value={form.name} onChange={set("name")} className={inputCls} />
              <input data-testid="request-input-company" aria-label={t.request.company} placeholder={t.request.company} value={form.company} onChange={set("company")} className={inputCls} />
              <input data-testid="request-input-email" type="email" aria-label={t.request.email} placeholder={t.request.email} value={form.email} onChange={set("email")} className={inputCls} />
              <input data-testid="request-input-phone" aria-label={t.request.phone} placeholder={t.request.phone} value={form.phone} onChange={set("phone")} className={inputCls} />
              <select data-testid="request-select-mineral" required aria-label={t.request.mineral} value={form.mineral} onChange={set("mineral")} className={`${inputCls} appearance-none`}>
                <option value="" disabled className="bg-[#151B24]">{t.request.selectMineral}</option>
                {t.minerals.items.map((m) => (
                  <option key={m.id} value={m.name} className="bg-[#151B24]">{m.name}</option>
                ))}
                <option value={t.request.otherMineral} className="bg-[#151B24]">{t.request.otherMineral}</option>
              </select>
              <input data-testid="request-input-qty" aria-label={t.request.qty} placeholder={t.request.qty} value={form.qty} onChange={set("qty")} className={inputCls} />
            </div>
            <textarea
              data-testid="request-input-message"
              required
              aria-label={t.request.message}
              placeholder={t.request.message}
              rows={4}
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-4 resize-none`}
            />
            <button
              data-testid="request-submit-button"
              type="submit"
              className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#C9A227] px-10 py-4 text-sm font-bold tracking-[0.15em] text-[#07111F] hover:bg-[#F5F7FA] hover:shadow-[0_0_30px_rgba(201,162,39,0.4)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300"
            >
              <Send size={16} /> {t.request.submit}
            </button>
            <p className="mt-4 text-xs text-[#EDF1F6]/60">{t.request.note}</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
